import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getFirestore, doc, updateDoc, addDoc, collection } from 'firebase/firestore';

const HeroSearchModal = ({ isOpen, onClose, initialSearchQuery = '', isDarkMode = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    searchQuery: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [documentId, setDocumentId] = useState(null);

  // Create Firebase document immediately when modal opens and pre-populate search query
  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        searchQuery: initialSearchQuery || ''
      }));
      setShowSuccess(false);
      setFormErrors({});
      
      // Create document in Firebase immediately
      createInitialDocument(initialSearchQuery);
    }
  }, [isOpen, initialSearchQuery]);

  const createInitialDocument = async (searchQuery) => {
    try {
      const submissionData = {
        searchQuery: searchQuery || '',
        searchTimestamp: new Date().toISOString(),
        source: 'employer_hero_search_modal_request',
        status: 'search_initiated',
        userInfo: null, // Will be filled later if user submits
        completedAt: null // Will be filled later if user submits
      };

      const db = getFirestore();
      const docRef = await addDoc(collection(db, "employer_request"), submissionData);
      
      setDocumentId(docRef.id);

    } catch (error) {
      console.error('Error creating initial search document:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!formData.searchQuery.trim()) {
      errors.searchQuery = 'Please describe what you\'re looking for';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (!documentId) {
        console.error('No document ID available for update');
        setIsSubmitting(false);
        return;
      }

      // Update the existing document with user information
      const db = getFirestore();
      const docRef = doc(db, "employer_request", documentId);
      
      const updateData = {
        userInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          searchQuery: formData.searchQuery,
        },
        searchQuery: formData.searchQuery, // Update in case user modified it
        completedAt: new Date().toISOString(),
        status: 'completed'
      };

      await updateDoc(docRef, updateData);
      
      setShowSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        searchQuery: ''
      });
    } catch (error) {
      console.error('Error updating document:', error);
      // Still show success to user but log the error
      setShowSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        searchQuery: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setShowSuccess(false);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      searchQuery: ''
    });
    setFormErrors({});
    setDocumentId(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className={`relative w-full max-w-2xl rounded-2xl p-8 shadow-2xl ${
          isDarkMode ? 'bg-gray-800' : 'bg-white'
        }`}>
          <div className="flex items-center justify-between mb-6">
            <DialogTitle className={`text-2xl font-bold ${
              isDarkMode ? 'text-white' : 'text-gray-900'
            }`}>
Access top performers in our network            </DialogTitle>
            <button
              onClick={handleClose}
              className={`rounded-full p-2 hover:bg-gray-100 transition-colors ${
                isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
            >
              <XMarkIcon className={`h-5 w-5 ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`} />
            </button>
          </div>

          {showSuccess ? (
            <div className="text-center py-8">
              <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full mb-6 ${
                isDarkMode ? 'bg-green-900/20' : 'bg-green-100'
              }`}>
                <CheckIcon className={`h-8 w-8 ${
                  isDarkMode ? 'text-green-400' : 'text-green-600'
                }`} />
              </div>
              <h3 className={`text-2xl font-bold mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Request Received! 🎉
              </h3>
              <p className={`text-lg mb-4 ${
                isDarkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Access top performers in our network
              </p>
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                We'll review your request and get back to you within 24 hours.
              </p>
              <button
                onClick={handleClose}
                className="mt-6 rounded-xl bg-purple-600 px-6 py-3 text-base font-semibold text-white hover:bg-purple-700 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* First Name and Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className={`block text-sm font-semibold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`block w-full rounded-xl border-0 px-4 py-3 shadow-sm ring-1 ring-inset transition-all duration-200 ${
                      formErrors.firstName 
                        ? 'ring-red-500 bg-red-50/50' 
                        : `ring-gray-300 hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 ${
                            isDarkMode 
                              ? 'bg-gray-700 text-white placeholder:text-gray-400' 
                              : 'bg-gray-50/50 text-gray-900 placeholder:text-gray-400'
                          }`
                    } text-base`}
                    placeholder="Enter your first name"
                  />
                  {formErrors.firstName && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      {formErrors.firstName}
                    </p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className={`block text-sm font-semibold mb-3 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`block w-full rounded-xl border-0 px-4 py-3 shadow-sm ring-1 ring-inset transition-all duration-200 ${
                      formErrors.lastName 
                        ? 'ring-red-500 bg-red-50/50' 
                        : `ring-gray-300 hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 ${
                            isDarkMode 
                              ? 'bg-gray-700 text-white placeholder:text-gray-400' 
                              : 'bg-gray-50/50 text-gray-900 placeholder:text-gray-400'
                          }`
                    } text-base`}
                    placeholder="Enter your last name"
                  />
                  {formErrors.lastName && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                      </svg>
                      {formErrors.lastName}
                    </p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className={`block text-sm font-semibold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  Work Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`block w-full rounded-xl border-0 px-4 py-3 shadow-sm ring-1 ring-inset transition-all duration-200 ${
                    formErrors.email 
                      ? 'ring-red-500 bg-red-50/50' 
                      : `ring-gray-300 hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white placeholder:text-gray-400' 
                            : 'bg-gray-50/50 text-gray-900 placeholder:text-gray-400'
                        }`
                  } text-base`}
                  placeholder="Enter your work email"
                />
                {formErrors.email && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    {formErrors.email}
                  </p>
                )}
              </div>

              {/* What you're looking for */}
              <div>
                <label htmlFor="searchQuery" className={`block text-sm font-semibold mb-3 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  What you're looking for <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="searchQuery"
                  name="searchQuery"
                  rows={3}
                  required
                  value={formData.searchQuery}
                  onChange={handleInputChange}
                  className={`block w-full rounded-xl border-0 px-4 py-3 shadow-sm ring-1 ring-inset transition-all duration-200 resize-none ${
                    formErrors.searchQuery 
                      ? 'ring-red-500 bg-red-50/50' 
                      : `ring-gray-300 hover:ring-purple-300 focus:ring-2 focus:ring-purple-600 ${
                          isDarkMode 
                            ? 'bg-gray-700 text-white placeholder:text-gray-400' 
                            : 'bg-gray-50/50 text-gray-900 placeholder:text-gray-400'
                        }`
                  } text-base`}
                  placeholder="Describe your ideal candidate..."
                />
                {formErrors.searchQuery && (
                  <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    {formErrors.searchQuery}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`group relative w-full rounded-xl px-6 py-4 text-base font-semibold text-white shadow-lg transition-all duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 ${
                    isSubmitting 
                      ? 'bg-gray-400 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
                  }`}
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center gap-3">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting your request...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Submit Request</span>
                      <svg className="h-5 w-5 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </div>
                  )}
                </button>
              </div>
            </form>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default HeroSearchModal; 